// frontend/src/pages/settings/users-roles/RoleEditor.tsx
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, Copy, RotateCcw, Save, Trash2, X } from 'lucide-react';
import { CATALOG, CAP_BY_KEY, isKeyLevel, type CatalogCapability } from '../../../lib/rbac';
import { AreaSection } from './AreaSection';
import { rbacApi } from './api';
import type { AreaFlags, RoleDetail, RoleDraft } from './types';

interface Props {
  role: RoleDetail;
  onSaved: (r: RoleDetail) => void;
  onDeleted: () => void;
  onDuplicate: (r: RoleDetail) => void;
}

function toDraft(r: RoleDetail): RoleDraft {
  const grants: Record<string, boolean> = {};
  // Se ignoran los grants de capacidades que ya no están en el catálogo: si no, el rol
  // quedaría imposible de guardar (el backend rechaza claves desconocidas) y la excepción
  // sería invisible en pantalla.
  for (const g of r.grants) if (CAP_BY_KEY.has(g.capability)) grants[g.capability] = g.granted;
  const areas: Record<string, AreaFlags> = {};
  for (const a of CATALOG.areas) areas[a.key] = r.areas[a.key] ?? { view_all: false, operate_all: false };
  return { name: r.name, description: r.description ?? '', areas, grants };
}

function inherited(c: CatalogCapability, areas: Record<string, AreaFlags>): boolean {
  if (isKeyLevel(c.level)) return false;
  const f = areas[c.area];
  if (!f) return false;
  return (c.level === 'view' && f.view_all) || (c.level === 'operate' && f.operate_all);
}

export function RoleEditor({ role, onSaved, onDeleted, onDuplicate }: Props) {
  const [draft, setDraft] = useState<RoleDraft>(() => toDraft(role));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  // Sin setMsg(null): el editor está keyeado por selected.id, así que cambiar de rol lo re-monta
  // y limpia el mensaje solo. Borrarlo acá tapaba el cartel de "Rol guardado" (onSaved cambia role).
  useEffect(() => { setDraft(toDraft(role)); }, [role]);

  const readOnly = role.is_admin;
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(toDraft(role)), [draft, role]);

  /** Lo que muestra la tilde y lo que el clic invierte: grant explícito, o lo heredado del área. */
  const base = (capKey: string): boolean => {
    if (role.is_admin) return true;
    const c = CAP_BY_KEY.get(capKey);
    if (!c) return false;
    return draft.grants[capKey] ?? inherited(c, draft.areas);
  };

  /** Lo que el rol realmente puede hacer: base + prerequisitos cumplidos. Alimenta contadores y notas. */
  const effective = (capKey: string): boolean => {
    if (role.is_admin) return true;
    const c = CAP_BY_KEY.get(capKey);
    if (!c) return false;
    if (!base(capKey)) return false;
    if (c.requires && !effective(c.requires)) return false;
    // Misma regla que el backend: una pestaña no vale nada sin el "Ver" de su área.
    return !c.key.includes('.tab.') || effective(`${c.area}.view`);
  };

  const toggleCap = (c: CatalogCapability) => {
    if (readOnly) return;
    const inh = inherited(c, draft.areas);
    const current = draft.grants[c.key] ?? inh;
    const next = !current;
    const grants = { ...draft.grants };
    if (next === inh) delete grants[c.key]; else grants[c.key] = next;
    setDraft({ ...draft, grants });
  };

  const toggleArea = (areaKey: string, level: 'view' | 'operate', value: boolean) => {
    if (readOnly) return;
    const f = { ...draft.areas[areaKey] };
    if (level === 'view') { f.view_all = value; if (!value) f.operate_all = false; }
    else { f.operate_all = value; if (value) f.view_all = true; }
    const grants = { ...draft.grants };
    for (const c of CATALOG.capabilities) {
      if (c.area !== areaKey) continue;
      if (c.level === level) { delete grants[c.key]; continue; }
      // El cabezal arrastra el otro nivel (Ver off apaga Operar; Operar on enciende Ver):
      // los grants explícitos del nivel arrastrado también se limpian, para que la pantalla
      // muestre exactamente lo que quedó configurado.
      if (level === 'view' && !value && c.level === 'operate') delete grants[c.key];
      if (level === 'operate' && value && c.level === 'view') delete grants[c.key];
    }
    setDraft({ ...draft, areas: { ...draft.areas, [areaKey]: f }, grants });
  };

  const save = async () => {
    if (role.user_count > 0 && !window.confirm(`Este rol tiene ${role.user_count} usuario(s). Los cambios aplican en menos de 30 segundos. ¿Guardar?`)) return;
    setSaving(true); setMsg(null);
    const res = await rbacApi.updateRole(role.id, {
      name: draft.name, description: draft.description,
      areas: draft.areas,
      grants: Object.entries(draft.grants).map(([capability, granted]) => ({ capability, granted })),
    });
    setSaving(false);
    if (res.success && res.data) { setMsg({ type: 'ok', text: 'Rol guardado' }); onSaved(res.data); }
    else setMsg({ type: 'err', text: res.error || 'No se pudo guardar' });
  };

  const reset = async () => {
    if (!window.confirm('¿Restaurar este rol a los valores sugeridos? Se pierden los cambios hechos.')) return;
    const res = await rbacApi.resetRole(role.id);
    if (res.success && res.data) { setMsg({ type: 'ok', text: 'Rol restaurado' }); onSaved(res.data); }
    else setMsg({ type: 'err', text: res.error || 'No se pudo restaurar' });
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar el rol «${role.name}»?`)) return;
    const res = await rbacApi.deleteRole(role.id);
    if (res.success) onDeleted(); else setMsg({ type: 'err', text: res.error || 'No se pudo eliminar' });
  };

  const duplicate = async () => {
    const name = window.prompt('Nombre del rol nuevo', `${role.name} (copia)`);
    if (!name) return;
    const res = await rbacApi.createRole({ name, description: draft.description, copy_from: role.id });
    if (res.success && res.data) onDuplicate(res.data); else setMsg({ type: 'err', text: res.error || 'No se pudo duplicar' });
  };

  return (
    <div className="flex-1 space-y-4 min-w-0">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px] space-y-2">
          <input
            value={draft.name} disabled={readOnly}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="w-full text-lg font-semibold bg-transparent border-b border-[var(--color-border)] focus:outline-none text-[var(--color-text)]"
          />
          <input
            value={draft.description} disabled={readOnly} placeholder="Descripción (para quien asigna el rol)"
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            className="w-full text-xs bg-transparent border-b border-[var(--color-border)] focus:outline-none text-[var(--color-text-secondary)]"
          />
          {readOnly && <p className="text-xs text-[var(--color-text-muted)]">El Administrador siempre tiene todo. No se edita.</p>}
        </div>
        <div className="flex items-center gap-2">
          {/* El Administrador no guarda permisos (su poder es implícito): una copia saldría vacía. */}
          {!role.is_admin && (
            <button onClick={duplicate} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]"><Copy size={13} /> Duplicar</button>
          )}
          {role.is_system && !readOnly && (
            <button onClick={reset} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]"><RotateCcw size={13} /> Restaurar sugerido</button>
          )}
          {!role.is_system && (
            <button onClick={remove} disabled={role.user_count > 0} title={role.user_count > 0 ? 'Tiene usuarios asignados' : ''} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-red-500/30 text-red-600 hover:bg-red-500/10 disabled:opacity-40"><Trash2 size={13} /> Eliminar</button>
          )}
          {!readOnly && (
            <>
              <button onClick={() => setDraft(toDraft(role))} disabled={!dirty} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-[var(--color-border)] disabled:opacity-40"><X size={13} /> Descartar</button>
              <button onClick={save} disabled={!dirty || saving} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-[var(--color-primary)] text-white disabled:opacity-40"><Save size={13} /> {saving ? 'Guardando…' : 'Guardar'}</button>
            </>
          )}
        </div>
      </div>

      {dirty && <div className="text-xs text-amber-600">Cambios sin guardar</div>}
      {msg && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${msg.type === 'ok' ? 'bg-green-500/10 border border-green-500/30 text-green-600' : 'bg-red-500/10 border border-red-500/30 text-red-600'}`}>
          {msg.type === 'ok' ? <CheckCircle size={14} /> : <AlertCircle size={14} />} {msg.text}
        </div>
      )}

      <div className="space-y-2">
        {CATALOG.areas.map((a) => (
          <AreaSection
            key={a.key}
            areaKey={a.key}
            label={a.label}
            caps={CATALOG.capabilities.filter((c) => c.area === a.key)}
            flags={draft.areas[a.key]}
            grants={draft.grants}
            base={base}
            effective={effective}
            readOnly={readOnly}
            onToggleArea={(level, v) => toggleArea(a.key, level, v)}
            onToggleCap={toggleCap}
          />
        ))}
      </div>
      <p className="text-[10px] text-[var(--color-text-muted)]">
        «Ver» y «Operar» tildan en bloque las funciones comunes del área y también las que se agreguen en el futuro. Las funciones con ícono son llaves especiales: se tildan una por una y nunca en bloque.
      </p>
    </div>
  );
}
